import type { UserProfile } from '../../domain/users/types.no-deps'
import type { TeamWithCount } from '../../domain/teams/types.no-deps'
import Fuse from 'fuse.js'
import type { SupportedLanguage } from '../../i18n/i18n.no-deps'

export type User = UserProfile & { isDeleted?: boolean }
export type Team = TeamWithCount & { members?: number[] }
export type Assignable = User | Team

export type UserAssigneeId = { userId: number }
export type TeamAssigneeId = { teamId: number }

export type AssigneeId = UserAssigneeId | TeamAssigneeId

export type AssigneeIds = { users: number[]; teams: number[] }

export function fuzzyFilter<A extends { name: string }>(assignees: A[], search: string) {
    if (search === '') {
        return assignees
    }
    const fuse = new Fuse(assignees, { keys: ['name'], threshold: 0.3 })
    return fuse.search(search).map((result) => result.item)
}

export function isTeam(assignee: Assignable): assignee is Team {
    return assignee && 'id' in assignee
}

export function isUser(assignee: Assignable): assignee is User {
    return assignee && 'userId' in assignee
}

export function isTeamAssigneeId(assigneeId: AssigneeId): assigneeId is TeamAssigneeId {
    return 'teamId' in assigneeId
}

export function isUserAssigneeId(assigneeId: AssigneeId): assigneeId is UserAssigneeId {
    return 'userId' in assigneeId
}

export function getAssigneeName(assignee?: Assignable) {
    return assignee?.name
}

export function getAssigneeId(assignee: Assignable) {
    return isTeam(assignee) ? assignee.id : isUser(assignee) ? assignee.userId : null
}

export function getAssigneeTeamNames(_: SupportedLanguage, assignee: User): string {
    const andTranslations = window.i18n('shared:common.and')

    const moreTranslations = window.i18n('shared:common.more')

    if (assignee.teams?.length) {
        const teams = assignee.teams.map((team) => team.name)

        if (teams.length === 1) return teams[0]
        else if (teams.length === 2) return `${teams[0]} ${andTranslations} ${teams[1]}`
        else {
            return `${teams[0]}, ${teams[1]} ${andTranslations} +${teams.length - 2} ${moreTranslations}`
        }
    } else return ''
}

export function getTeamCountText(_: SupportedLanguage, assignee: Team): string {
    const peopleTranslation = window.i18n('shared:common.people')
    const personTranslation = window.i18n('shared:common.person')

    return assignee.count === 1 ? `1 ${personTranslation}` : `${assignee.count} ${peopleTranslation}`
}

/**
 * Sorts an array of assignable items alphabetically by their name property.
 *
 * @param assignables - The array of assignable items to be sorted.
 * @returns The sorted array of assignable items.
 */
export function sortAssignablesAlphabetically(assignables: Assignable[]): Assignable[] {
    return assignables.sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Moves the specified user to the front of the assignables list.
 *
 * @param assignables - The list of assignable items.
 * @param currentUserId - The id of the user to move to the front of the list.
 * @returns A new list with the specified user moved to the front.
 *
 * @remarks
 * - If the user is not found in the list, the original list is returned unchanged.
 *
 */
export function moveUserToFront(assignables: Assignable[], currentUserId: number): Assignable[] {
    // Find the index of the current user in the list
    const currentUserIndex = assignables.findIndex(
        (assignable) => isUser(assignable) && assignable.userId === currentUserId
    )

    // If the current user is not in the list, return the list as it is
    if (currentUserIndex === -1) {
        return assignables
    }

    // Otherwise, return the list with the current user added to the front
    return [
        assignables[currentUserIndex],
        ...assignables.slice(0, currentUserIndex),
        ...assignables.slice(currentUserIndex + 1),
    ]
}
